import { useState, useCallback } from 'react';
import { Message } from '../../../types/chat';
import { storage } from '../../../lib/storage';
import { notify } from '../../../utils/toast';
// import { useAuth } from '../useAuth';
import { getAuthData } from "../../../contexts/AuthContext";
import { getAiApiUrl } from '../../../config';
import { getAvatarUrl } from '../../../utils/avatar';


export const useMessages = () => {
  const [messages, setMessages] = useState<Message[]>([]);


  const loadMessages = useCallback(async (walletAddress: string, conversationId: string) => {
    try {
      const profile = await storage.getProfile(walletAddress);
      const avatarUrl = profile ? getAvatarUrl(profile) : undefined;

      // Try to fetch messages from API first
      try {
        const auth = await getAuthData();
        const response = await fetch(`${getAiApiUrl()}/messages?user=${auth.did.toLowerCase().replaceAll('_uuid', ':uuid').replaceAll('_addr', ':addr').replaceAll('_asa', ':asa').replaceAll('_app', ':app').replaceAll('_txn', ':txn')}&conversation_id=${conversationId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.jwt}`
          },
          body: JSON.stringify({
            walletAddress: auth.walletAddress,
            nonce: auth.nonce,
            conversation_id: conversationId
          })
        });

        if (response.ok) {
          const data = await response.json();

          // Clear existing messages for this conversation to ensure we overwrite with the latest data
          try {
            // Get all conversations for this wallet
            const conversations = await storage.getConversations(auth.walletAddress);

            // Find the specific conversation by ID
            const existingConversation = conversations.find(conv => conv.id === conversationId);

            if (existingConversation?.messageIds?.length > 0) {
              console.log(`[Messages] Clearing ${existingConversation.messageIds.length} existing messages for conversation ${conversationId}`);

              // Remove all existing messages
              for (const messageId of existingConversation.messageIds) {
                await storage.removeMessage(messageId);
              }

              // Update conversation with empty message list
              await storage.updateConversation(auth.walletAddress, conversationId, {
                messageIds: []
              });
            }
          } catch (error) {
            console.error('[Messages] Error clearing existing messages:', error);
            // Continue with processing even if clearing fails
          }

          // Create two messages for each API record - one for user query and one for assistant answer
          const formattedMessages: Message[] = [];
          for (const msg of data.data) {
            // Add user message
            if (msg.query) {
              formattedMessages.push({
                id: `${msg.id}-user`,
                content: msg.query,
                sender: 'user',
                created_at: msg.created_at || new Date(msg.created_at * 1000).toISOString(),
                metadata: msg.metadata || undefined,
                avatar: avatarUrl
              });
            }

            // Add assistant message
            if (msg.answer) {
              let metadata = msg.metadata;
              if (!metadata) {
                metadata = await storage.getMessageMetadata(msg.id);
              }
              formattedMessages.push({
                id: `${msg.id}`,
                content: msg.answer,
                sender: 'assistant',
                created_at: new Date(msg.created_at * 1000).toISOString(),
                avatar: '/images/Agent.png',
                metadata: metadata || null
              });
            }
          }

          // Filter out user messages that don't have a corresponding assistant response
          const filteredMessages: Message[] = [];
          let lastSender: string | null = null;

          for (let i = 0; i < formattedMessages.length; i++) {
            const msg = formattedMessages[i];

            // Always keep assistant messages
            if (msg && msg.sender === 'assistant') {
              filteredMessages.push(msg);
              lastSender = 'assistant';
              continue;
            }

            // For user messages:
            if (msg && msg.sender === 'user') {
              // Skip if this is the last message (no response)
              // if (i === formattedMessages.length - 1) {
              //   continue;
              // }

              // Skip if the next message is also from a user (no response to this message)
              // if (i < formattedMessages.length - 1 && formattedMessages[i + 1].sender === 'user') {
              //   continue;
              // }

              // Otherwise keep the user message
              filteredMessages.push(msg);
              lastSender = 'user';
            }
          }

          // Store messages in local storage
          for (const message of filteredMessages) {
            await storage.addMessageToConversation(
              auth.walletAddress,
              conversationId,
              message
            );
          }

          setMessages(filteredMessages);
          console.log('[Messages] Loaded messages from API:', filteredMessages.length);
          return true;
        } else {
          console.warn('[Messages] Failed to fetch messages from API, falling back to local storage');
          throw new Error('Failed to fetch messages from API');
        }
      } catch (error) {
        console.warn('[Messages] Error fetching messages from API, falling back to local storage:', error);

        // If API fetch fails, try to load from local storage
        const storedMessages = await storage.getConversationMessages(walletAddress, conversationId);
        console.log('[Messages] Loading messages from local storage:', storedMessages);

        if (storedMessages && storedMessages.length > 0) {
          // Ensure all messages have content and add avatar for user messages
          const validatedMessages = storedMessages.map(msg => ({
            ...msg,
            content: msg.content || '',
            metadata: msg.metadata || {},
            avatar: msg.sender === 'user' ? avatarUrl : '/images/Agent.png'
          }));

          // Filter out user messages that don't have a corresponding assistant response
          const filteredMessages: Message[] = [];
          let lastSender: string | null = null;

          for (let i = 0; i < validatedMessages.length; i++) {
            const msg = validatedMessages[i];

            // Always keep assistant messages
            if (msg.sender === 'assistant') {
              filteredMessages.push(msg);
              lastSender = 'assistant';
              continue;
            }

            // For user messages:
            if (msg.sender === 'user') {
              // Skip if this is the last message (no response)
              // if (i === validatedMessages.length - 1) {
              //   continue;
              // }

              // Skip if the next message is also from a user (no response to this message)
              // if (i < validatedMessages.length - 1 && validatedMessages[i + 1].sender === 'user') {
              //   continue;
              // }

              // Otherwise keep the user message
              filteredMessages.push(msg);
              lastSender = 'user';
            }
          }

          console.log('[Messages] Loaded messages from storage:', storedMessages.length);
          console.log('[Messages] Filtered messages:', filteredMessages.length);
          setMessages(filteredMessages);
          return true;
        }

        // If no messages in local storage either, return false
        return false;
      }

      // This code is now handled in the try block above
      return false;
    } catch (error: any) {
      console.error('Error loading messages:', error);
      notify.error(error.message || 'Failed to load messages');
      return false;
    }
  }, [getAuthData]);

  return {
    messages,
    setMessages,
    loadMessages
  };
};
